import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { FaCheckCircle, FaChartLine, FaShieldAlt, FaFileInvoiceDollar, FaBalanceScale, FaUserTie } from 'react-icons/fa'

const FinancialStatementAudit = () => {
  return (
    <>
      <Helmet>
        <title>Financial Statement Audit Services - Proof of Reserves Auditor</title>
        <meta name="description" content="GAAP and IFRS-compliant financial statement audits for cryptocurrency and digital asset companies by crypto-native CPAs." />
      </Helmet>

      <div className="service-page">
        {/* Hero Section */}
        <section className="relative section-padding bg-gradient-to-br from-primary to-primary-800 text-white">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="/cpa.jpg"
              alt="CPA Services Background"
              className="w-full h-full object-cover"
            />
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          </div>
          <div className="container-custom relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Financial Statement Audit Services</h1>
            <p className="text-xl text-gray-200 max-w-3xl mb-8">
              Professional financial statement audits tailored for cryptocurrency and digital asset companies
            </p>
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="flex items-center space-x-2">
                <FaCheckCircle className="text-accent" />
                <span>GAAP & IFRS Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <FaCheckCircle className="text-accent" />
                <span>Crypto-Native CPAs</span>
              </div>
              <div className="flex items-center space-x-2">
                <FaCheckCircle className="text-accent" />
                <span>Regulatory Ready</span>
              </div>
            </div>
            <Link to="/contact" className="btn-primary bg-accent hover:bg-accent-dark">
              Request a Quote
            </Link>
          </div>
        </section>

        {/* Overview Section */}
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Overview</h2>
            <p className="text-lg text-gray-600 mb-6">
              Our financial statement audit services provide independent examination and verification of your company's financial statements, ensuring compliance with Generally Accepted Accounting Principles (GAAP) or International Financial Reporting Standards (IFRS). Our team of crypto-native certified public accountants understands the unique complexities of digital asset accounting, from token valuations to DeFi protocol revenue recognition.
            </p>
            <p className="text-lg text-gray-600">
              Whether you're preparing for regulatory filings, securing institutional investment, or establishing credibility with stakeholders, our audits deliver the assurance and credibility your business needs.
            </p>
          </div>
        </section>

        {/* What We Audit */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="section-title text-center mb-12">What We Audit</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaBalanceScale className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Balance Sheet</h3>
                <p className="text-gray-600">
                  Comprehensive verification of assets (including digital assets), liabilities, and equity positions with proper valuation methodologies.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaChartLine className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Income Statement</h3>
                <p className="text-gray-600">
                  Examination of revenue streams, operating expenses, and profitability metrics specific to crypto business models.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaFileInvoiceDollar className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Cash Flow Statement</h3>
                <p className="text-gray-600">
                  Analysis of cash flows from operations, investing, and financing activities, including crypto-to-fiat conversions.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaShieldAlt className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Internal Controls</h3>
                <p className="text-gray-600">
                  Assessment of internal control systems over financial reporting, including wallet security and transaction authorization.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaUserTie className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Regulatory Compliance</h3>
                <p className="text-gray-600">
                  Verification of compliance with applicable accounting standards and regulatory requirements.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaCheckCircle className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Disclosures & Notes</h3>
                <p className="text-gray-600">
                  Review of footnotes and disclosures for completeness, accuracy, and compliance with reporting standards.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Key Features & Benefits</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg mb-1">Crypto-Native Expertise</h3>
                  <p className="text-gray-600">Our CPAs specialize in digital asset accounting and understand complex crypto transactions, DeFi protocols, and tokenomics.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg mb-1">Regulatory Compliance</h3>
                  <p className="text-gray-600">Audits structured to meet SEC, FINRA, state regulators, and international regulatory body requirements.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg mb-1">Investor-Ready Reports</h3>
                  <p className="text-gray-600">Professionally formatted audit opinions acceptable to VCs, institutional investors, and strategic partners.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg mb-1">Digital Asset Valuation</h3>
                  <p className="text-gray-600">Proper valuation methodologies for cryptocurrencies, tokens, NFTs, and other digital assets on your balance sheet.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg mb-1">Industry Best Practices</h3>
                  <p className="text-gray-600">Adherence to AICPA guidelines and emerging best practices for digital asset accounting.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg mb-1">Timely Delivery</h3>
                  <p className="text-gray-600">Efficient audit processes designed to meet your reporting deadlines and business timelines.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who Needs This */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="section-title text-center mb-12">Who Needs Financial Statement Audits?</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-primary">Companies Seeking Investment</h3>
                <p className="text-gray-600">
                  Institutional investors and VCs require audited financials before committing capital. Professional audits demonstrate financial credibility and operational maturity.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-primary">Regulatory Compliance</h3>
                <p className="text-gray-600">
                  Companies subject to regulatory oversight or seeking licenses need compliant financial statement audits to satisfy regulatory requirements.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-primary">Public Companies & Pre-IPO</h3>
                <p className="text-gray-600">
                  Public companies require annual audits, and companies preparing for public offerings need clean audit opinions to proceed with registration.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-primary">Banking Relationships</h3>
                <p className="text-gray-600">
                  Traditional banks often require audited financial statements to establish or maintain banking relationships for crypto companies.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Audit Process */}
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Our Audit Process</h2>
            <div className="space-y-6">
              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-2 text-primary">1. Planning & Risk Assessment</h3>
                <p className="text-gray-600">
                  We begin with comprehensive planning, understanding your business model, identifying risks, and developing a tailored audit approach.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-2 text-primary">2. Internal Control Evaluation</h3>
                <p className="text-gray-600">
                  Assessment of your internal controls over financial reporting, including wallet security, transaction approvals, and reconciliation procedures.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-2 text-primary">3. Substantive Testing</h3>
                <p className="text-gray-600">
                  Detailed testing of account balances, transactions, and disclosures using sampling methodologies and digital asset verification tools.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-2 text-primary">4. Digital Asset Verification</h3>
                <p className="text-gray-600">
                  On-chain verification of cryptocurrency holdings, wallet ownership validation, and proper valuation of digital assets.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-2 text-primary">5. Findings & Adjustments</h3>
                <p className="text-gray-600">
                  Communication of audit findings, proposed adjustments, and collaboration with management to resolve identified issues.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-2 text-primary">6. Opinion & Report Issuance</h3>
                <p className="text-gray-600">
                  Delivery of the final audit report with our professional opinion on the fairness of your financial statements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Schedule a consultation to discuss your financial statement audit needs and timeline.
            </p>
            <Link to="/contact" className="btn-primary bg-accent hover:bg-accent-dark">
              Schedule Consultation
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}

export default FinancialStatementAudit